import { useRefFn, useUpdateEffect } from '@fullstory/react-hooks';
import type { useEffect } from 'react';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * @template TOutput Output value within Observable.
 * @template TInputs A readonly tuple of all dependencies.
 *
 * @param useEffectFn useEffect or useLayoutEffect
 * @param init A pure function that, when applied to an Observable,
 * returns an Observable.
 * @param inputs An dependency array with fixed length. When one of the dependencies
 * changes the Observable in `init` will emit an array of all the dependencies.
 */
export function useObservableInternal<TOutput, TInputs extends Readonly<any[]>>(
  useEffectFn: typeof useEffect,
  init: (() => Observable<TOutput>) | ((inputs$: Observable<[...TInputs]>) => Observable<TOutput>),
  inputs?: [...TInputs],
): Observable<TOutput> {
  // Inputs$ Observable is only created when inputs argument was initially defined
  const initInputsRef = inputs ? () => new BehaviorSubject(inputs) : () => undefined;
  const inputs$ = useRefFn(initInputsRef).current;

  // Init function only receives the inputs$ Observable when inputs arg was initially defined
  const initSourceRef = inputs$ ? () => init(inputs$.asObservable()) : init;
  const source$ = useRefFn(initSourceRef as () => Observable<TOutput>).current;

  // Since the BehaviorSubject is initialized with an initial value,
  // we don't want to do re-emit that value on the first effect run.
  // We can use `useUpdateEffect` to skip the first run.
  useUpdateEffect(
    () => {
      // Only emit values if the inputs$ Observable was actually
      // initialized and we have a valid dependency array.
      if (inputs$ && Array.isArray(inputs)) {
        inputs$?.next(inputs);
      }
    },
    inputs ?? [],
    useEffectFn,
  );

  return source$;
}
